import React, { useMemo } from 'react'
import moment from 'moment'
import styles from './EventCard.module.scss'
import { ReactComponent as CalendarIcon } from '../../../assets/media/icons/icon-calendar.svg'
import { ReactComponent as FittingDayIcon } from '../../../assets/media/icons/icon-fitting-day.svg'
import { ReactComponent as DemoDayIcon } from '../../../assets/media/icons/icon-demo-day.svg'
import { ReactComponent as MobileFittingVan } from '../../../assets/media/icons/icon-mobile-fitting.svg'
import PropTypes from 'prop-types'
import FormatUtilities from '../../../utilities/FormatUtilities'
import { EVENT_TYPE_CODES } from '../../../constants'

const EventCard = ({ event, localizations }) => {
    const eventAppointmentType = useMemo(() => {
        return event.eventTypeCode === EVENT_TYPE_CODES.DEMO ? localizations.walkUpAppointments : localizations.appointmentBased
    }, [event])

    const eventDate = useMemo(() => {
        return FormatUtilities.formatDateRange(event.startDate, event.endDate)
    }, [event])

    const eventTime = useMemo(() => {
        if (!event.startTime || !event.endTime) {
            return ''
        }
        return `| ${FormatUtilities.formatTime(event.startTime)} - ${FormatUtilities.formatTime(event.endTime)}`
    }, [event])

    const eventIcon = useMemo(() => {
        if (event.mobileFittingVan) {
            return <MobileFittingVan />
        }

        return event.eventTypeCode === EVENT_TYPE_CODES.DEMO ? <DemoDayIcon /> : <FittingDayIcon />
    }, [event])

    const buttonProps = useMemo(() => {
        if (event.eventTypeCode === EVENT_TYPE_CODES.DEMO) {
            return {
                text: localizations.viewDetails,
                href: `${process.env.REACT_APP_EAS_WEBAPP_URL}${process.env.REACT_APP_EAS_WEBAPP_LANDING_PAGE_PATH.replace('{id}', event.id)}`,
                isExternal: false,
            }
        } else if (event.eventTypeCode === EVENT_TYPE_CODES.EXTERNAL) {
            return {
                text: localizations.bookAFitting,
                href: FormatUtilities.formatUrl(event?.externalUrl),
                isExternal: true,
            }
        } else {
            return {
                text: localizations.bookAFitting,
                href: `${process.env.REACT_APP_EAS_WEBAPP_URL}${process.env.REACT_APP_EAS_WEBAPP_CREATE_APPOINTMENT_PATH.replace('{id}', event.id)}`,
                isExternal: false,
            }
        }
    }, [event])

    const eventType = useMemo(() => {
        if (event.mobileFittingVan) {
            return localizations.pingMobileFittingExperience
        }

        return event.eventTypeName
    }, [event])

    return (
        <div className={styles.container}>
            <div className={styles.eventType}>
                {eventIcon} {eventType}
            </div>
            <div className={styles.appointmentType}>{eventAppointmentType}</div>
            <div className={styles.appointmentInfo}>
                <CalendarIcon />
                <div>
                    {eventDate} {eventTime}
                </div>
            </div>
            <div className={styles.eventButton}>
                <a
                    className={styles.eventLink}
                    href={buttonProps.href}
                    onClick={e => {
                        e.stopPropagation()

                        if (!buttonProps.isExternal) {
                            return
                        }

                        if (confirm(localizations.messages.externalLink)) {
                            return true
                        } else {
                            return e.preventDefault()
                        }
                    }}
                    target='_blank'
                    rel='noreferrer nofollow'
                    aria-label={`${buttonProps.text} for ${eventType} on ${moment(event?.startDate).format('MMM D, YYYY')}`}
                >
                    {buttonProps.text}
                </a>
            </div>
        </div>
    )
}

EventCard.propTypes = {
    event: PropTypes.object,
    localizations: PropTypes.object,
}

export default EventCard
